/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else if (this.hasAttribute('static-visible')) {
        this.#applyStaticOverflowState();
      } else {
        this.#reflowItems();
      }
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer')) {
      const deferredReflow = async () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        await this.#waitForStyles();
        this.#initialize();
      };
      const { schedule } = this;

      const requestIdleCallback =
        typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;

      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      if (this.hasAttribute('static-visible')) {
        this.#initializeStatic();
      } else {
        await this.#waitForStyles();
        this.#initialize();
      }
    } else {
      // Not all element children has been parsed yet, try again in the next tick
      // <overflow-list> is a special case as critical.js can execute before DOMContentLoaded
      setTimeout(() => {
        if (this.hasAttribute('static-visible')) {
          this.#initializeStatic();
        } else {
          this.#waitForStyles().then(() => this.#initialize());
        }
      }, 0);
    }
  }

  #waitForStyles() {
    /** @type {HTMLLinkElement | null | undefined} */
    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

    // No styles or styles are already loaded.
    if (!styles || styles.sheet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      styles.addEventListener('load', resolve);
    });
  }

  /**
   * Initialize the element with a server-side visible limit (product-card swatches).
   */
  #initializeStatic() {
    this.#setupRefs();

    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        if (this.hasAttribute('static-visible')) {
          this.#applyStaticOverflowState();
          return;
        }

        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    this.#applyStaticOverflowState();
  }

  /**
   * Initialize the element
   */
  #initialize() {
    this.#setupRefs();

    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        if (this.hasAttribute('static-visible')) {
          this.#applyStaticOverflowState();
          return;
        }

        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    this.#reflowItems();
  }

  /**
   * @returns {void}
   */
  #setupRefs() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };
  }

  /**
   * Keep server-rendered swatch slots; only sync the +N counter and more button.
   */
  #applyStaticOverflowState() {
    const { overflowSlot, moreSlot, list } = this.#refs;
    const overflowCount = overflowSlot.assignedElements().length;

    if (overflowCount > 0) {
      moreSlot.hidden = false;
      list.style.setProperty('counter-reset', `overflow-count ${overflowCount}`);
      this.style.setProperty('--overflow-count', `${overflowCount}`);
    } else {
      moreSlot.hidden = true;
      this.style.setProperty('--overflow-count', '0');
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.hasAttribute('static-visible')) return;

    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder, list } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
    list.style.setProperty('flex-wrap', 'wrap');
    list.style.setProperty('overflow', 'visible');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    const lastElement = elements[elements.length - 1];

    if (!lastElement) {
      this.#resizeObserver.observe(this);
      this.#mutationObserver.observe(this, { childList: true });
      return;
    }

    const rootRect = list.getBoundingClientRect();
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    /** @type {Element[]} */
    let visibleElements = [];
    /** @type {Element[]} */
    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};
    if (height) list.style.height = `${height}px`;

    // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row.
    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    if (lastVisibleElement) {
      lastVisibleElement.style.setProperty('order', '-1');
    }

    const moreSlotRect = moreSlot.getBoundingClientRect();

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreSlotRect.top) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        hasOverflow = true;
        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    });

    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }

    if (lastVisibleElement) {
      lastVisibleElement.style.removeProperty('order');
    }

    // If we have a lastVisibleElement, ensure it's in the last visible position
    if (lastVisibleElement) {
      const visibleIndex = visibleElements.indexOf(lastVisibleElement);
      if (visibleIndex !== -1) {
        visibleElements.splice(visibleIndex, 1);
        visibleElements.push(lastVisibleElement);
      }
    }

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');
    list.style.removeProperty('height');

    hasOverflow && this.#updateMinimumReached(visibleElements);

    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  if (!customElements.get('overflow-list')) customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
/**
 * @param {Element} element
 * @param {Element | null} headerGroup
 * @returns {number}
 */
function getHeaderGroupChildContribution(element, headerGroup) {
  if (!(element instanceof HTMLElement)) return 0;

  const height = element.offsetHeight;
  if (height <= 0) return 0;

  if (element.classList.contains('announcement-bar-stack')) {
    return height;
  }

  if (
    element.classList.contains('announcement-bar-section') &&
    element.querySelector('.announcement-bar--dismissible') &&
    !element.closest('.announcement-bar-stack')
  ) {
    const headerSection = headerGroup?.querySelector('.header-section');
    let sibling = element.nextElementSibling;

    while (sibling && sibling !== headerSection) {
      if (
        sibling instanceof HTMLElement &&
        sibling.classList.contains('announcement-bar-section') &&
        !sibling.querySelector('.announcement-bar--dismissible')
      ) {
        return 0;
      }

      if (
        sibling instanceof HTMLElement &&
        sibling.classList.contains('announcement-bar-section') &&
        sibling.querySelector('.announcement-bar--dismissible')
      ) {
        break;
      }

      sibling = sibling.nextElementSibling;
    }
  }

  return height;
}

export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += getHeaderGroupChildContribution(element, headerGroup);
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Calculates the height of announcement-bar elements that appear above the header.
 * Returns 0 if the announcement-bar is below the header or doesn't exist.
 */
export function calculateAnnouncementBarHeightAbove(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup || !header) return 0;

  // Find the header's parent section wrapper (if it exists) or use the header itself
  const headerSection = header.closest('.header-section') || header;
  
  // Get all direct children of header-group
  const children = Array.from(headerGroup.children);
  
  // Find the index of the header section in the children array
  let headerIndex = children.indexOf(headerSection);
  
  // If header section not found, try finding the header element directly
  if (headerIndex === -1) {
    headerIndex = children.indexOf(header);
  }
  
  // Alternative approach: if header not found in children, use previousElementSibling
  if (headerIndex === -1) {
    // Find all previous siblings of the header section
    let prevSibling = headerSection.previousElementSibling;
    let announcementBarHeight = 0;
    while (prevSibling) {
      if (prevSibling.parentElement === headerGroup) {
        const height = getHeaderGroupChildContribution(prevSibling, headerGroup);
        if (height > 0) {
          announcementBarHeight += height;
        }
      }
      prevSibling = prevSibling.previousElementSibling;
    }
    return announcementBarHeight;
  }
  
  // If header is not found or is the first element, no announcement-bar above it
  if (headerIndex <= 0) return 0;

  // Calculate total height of all elements before the header
  let announcementBarHeight = 0;
  for (let i = 0; i < headerIndex; i++) {
    const element = children[i];
    if (element instanceof HTMLElement) {
      const height = getHeaderGroupChildContribution(element, headerGroup);
      if (height > 0) {
        announcementBarHeight += height;
      }
    }
  }

  return announcementBarHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
  const announcementBarHeightAbove = calculateAnnouncementBarHeightAbove(header, headerGroup);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  document.body.style.setProperty('--announcement-bar-height-above', `${announcementBarHeightAbove}px`);
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');

  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Run both functions on page load
updateAllHeaderCustomProperties();

// Also update on window load to ensure all elements are rendered
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    updateAllHeaderCustomProperties();
  });
}
